import {
  Component,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ViewChild,
  ElementRef,
  Input,
  OnChanges,
  SimpleChanges,
  HostListener,
  NgZone
} from '@angular/core';

interface Letter {
  char: string;
  color: string;
  originalColor: string;
  targetColor: string;
  colorProgress: number;
}

@Component({
  selector: 'nc-letter-glitch-background',
  templateUrl: './letter-glitch-background.component.html',
  styleUrls: ['./letter-glitch-background.component.less']
})
export class LetterGlitchBackgroundComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges {
  @ViewChild('container', { static: true }) containerRef!: ElementRef<HTMLDivElement>;
  @ViewChild('canvas', { static: true }) canvasRef!: ElementRef<HTMLCanvasElement>;

  /** 闪烁颜色数组 */
  @Input() ncGlitchColors: string[] = ['#2b4539', '#61dca3', '#61b3dc'];
  /** 闪烁频率（毫秒）（1-100） */
  @Input() ncGlitchSpeed: number = 50;

  /** 是否显示中心暗角 */
  private _centerVignette: boolean = false;
  @Input()
  set ncCenterVignette(val: boolean | string) {
    this._centerVignette = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncCenterVignette(): boolean {
    return this._centerVignette;
  }

  /** 是否显示边缘暗角 */
  private _outerVignette: boolean = true;
  @Input()
  set ncOuterVignette(val: boolean | string) {
    this._outerVignette = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncOuterVignette(): boolean {
    return this._outerVignette;
  }

  /** 是否开启平滑过渡 */
  private _smooth: boolean = true;
  @Input()
  set ncSmooth(val: boolean | string) {
    this._smooth = val !== null && val !== undefined && val !== false && val !== 'false';
  }
  get ncSmooth(): boolean {
    return this._smooth;
  }

  /** 字符集 */
  @Input() ncCharacters: string = 'ABCDEFGHIJKLMNOPQRSTUVWXYZ!@#$&*()-_+=/[]{};:<>.,0123456789';

  private animationRef: number | null = null;
  private letters: Letter[] = [];
  private grid = { columns: 0, rows: 0 };
  private context: CanvasRenderingContext2D | null = null;
  private lastGlitchTime: number = Date.now();
  private resizeTimeout: any;
  private activeLetterIndices: Set<number> = new Set();

  private fontSize = 16;
  private charWidth = 10;
  private charHeight = 20;

  constructor(private ngZone: NgZone) { }

  ngOnInit(): void { }

  ngAfterViewInit(): void {
    this.ngZone.runOutsideAngular(() => {
      this.initCanvas();
    });
  }

  ngOnDestroy(): void {
    if (this.animationRef !== null) {
      cancelAnimationFrame(this.animationRef);
    }
    if (this.resizeTimeout) {
      clearTimeout(this.resizeTimeout);
    }
  }

  ngOnChanges(changes: SimpleChanges): void {
    // 处理参数变更，如果有需要可以通过此方法重建或直接影响下一帧渲染
    if (changes['ncGlitchColors'] || changes['ncCharacters']) {
      // 在下一次闪烁时自然会应用新参数
    }
  }

  @HostListener('window:resize')
  onResize() {
    this.ngZone.runOutsideAngular(() => {
      clearTimeout(this.resizeTimeout);
      this.resizeTimeout = setTimeout(() => {
        if (this.animationRef !== null) {
          cancelAnimationFrame(this.animationRef);
        }
        this.resizeCanvas();
        this.animate();
      }, 100);
    });
  }

  private initCanvas() {
    const canvas = this.canvasRef.nativeElement;
    if (!canvas) return;

    this.context = canvas.getContext('2d');
    this.resizeCanvas();
    this.animate();
  }

  private getRandomChar(): string {
    const lettersAndSymbols = Array.from(this.ncCharacters);
    return lettersAndSymbols[Math.floor(Math.random() * lettersAndSymbols.length)];
  }

  private getRandomColor(): string {
    return this.ncGlitchColors[Math.floor(Math.random() * this.ncGlitchColors.length)];
  }

  private parseColor(color: string): { r: number, g: number, b: number } | null {
    if (color.startsWith('rgb')) {
      const match = color.match(/rgb\((\d+),\s*(\d+),\s*(\d+)\)/);
      if (match) {
        return {
          r: parseInt(match[1], 10),
          g: parseInt(match[2], 10),
          b: parseInt(match[3], 10)
        };
      }
    }

    let hex = color;
    const shorthandRegex = /^#?([a-f\d])([a-f\d])([a-f\d])$/i;
    hex = hex.replace(shorthandRegex, (m, r, g, b) => {
      return r + r + g + g + b + b;
    });

    const result = /^#?([a-f\d]{2})([a-f\d]{2})([a-f\d]{2})$/i.exec(hex);
    return result
      ? {
        r: parseInt(result[1], 16),
        g: parseInt(result[2], 16),
        b: parseInt(result[3], 16)
      }
      : null;
  }

  private interpolateColor(start: { r: number, g: number, b: number }, end: { r: number, g: number, b: number }, factor: number): string {
    const result = {
      r: Math.round(start.r + (end.r - start.r) * factor),
      g: Math.round(start.g + (end.g - start.g) * factor),
      b: Math.round(start.b + (end.b - start.b) * factor)
    };
    return `rgb(${result.r}, ${result.g}, ${result.b})`;
  }

  private calculateGrid(width: number, height: number): { columns: number, rows: number } {
    const columns = Math.ceil(width / this.charWidth);
    const rows = Math.ceil(height / this.charHeight);
    return { columns, rows };
  }

  private initializeLetters(columns: number, rows: number) {
    this.grid = { columns, rows };
    const totalLetters = columns * rows;
    this.letters = Array.from({ length: totalLetters }, () => {
      const color = this.getRandomColor();
      return {
        char: this.getRandomChar(),
        color: color,
        originalColor: color,
        targetColor: color,
        colorProgress: 1
      };
    });
  }

  private resizeCanvas() {
    const canvas = this.canvasRef.nativeElement;
    if (!canvas) return;
    const parent = canvas.parentElement;
    if (!parent) return;

    const dpr = window.devicePixelRatio || 1;
    const rect = parent.getBoundingClientRect();

    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;

    canvas.style.width = `${rect.width}px`;
    canvas.style.height = `${rect.height}px`;

    if (this.context) {
      this.context.setTransform(dpr, 0, 0, dpr, 0, 0);
    }

    const { columns, rows } = this.calculateGrid(rect.width, rect.height);
    this.initializeLetters(columns, rows);

    this.drawLetters();
  }

  private drawLetters(indicesToDraw?: Set<number>) {
    if (!this.context || this.letters.length === 0) return;
    const ctx = this.context;
    
    ctx.font = `${this.fontSize}px monospace`;
    ctx.textBaseline = 'top';

    if (!indicesToDraw) {
      const { width, height } = this.canvasRef.nativeElement.getBoundingClientRect();
      ctx.clearRect(0, 0, width, height);

      this.letters.forEach((letter, index) => {
        const x = (index % this.grid.columns) * this.charWidth;
        const y = Math.floor(index / this.grid.columns) * this.charHeight;
        ctx.fillStyle = letter.color;
        ctx.fillText(letter.char, x, y);
      });
      return;
    }

    indicesToDraw.forEach(index => {
      const letter = this.letters[index];
      const x = (index % this.grid.columns) * this.charWidth;
      const y = Math.floor(index / this.grid.columns) * this.charHeight;
      ctx.clearRect(x, y, this.charWidth, this.charHeight);
      ctx.fillStyle = letter.color;
      ctx.fillText(letter.char, x, y);
    });
  }

  private updateLetters() {
    if (!this.letters || this.letters.length === 0) return;

    const updateCount = Math.max(1, Math.floor(this.letters.length * 0.05));

    for (let i = 0; i < updateCount; i++) {
      const index = Math.floor(Math.random() * this.letters.length);
      if (!this.letters[index]) continue;

      this.letters[index].char = this.getRandomChar();
      this.letters[index].targetColor = this.getRandomColor();

      if (!this.ncSmooth) {
        this.letters[index].color = this.letters[index].targetColor;
        this.letters[index].originalColor = this.letters[index].targetColor;
        this.letters[index].colorProgress = 1;
        this.activeLetterIndices.add(index);
      } else {
        this.letters[index].originalColor = this.letters[index].color;
        this.letters[index].colorProgress = 0;
        this.activeLetterIndices.add(index);
      }
    }
  }

  private handleSmoothTransitions() {
    let needsRedraw = false;
    const itemsToDraw = new Set<number>();

    this.activeLetterIndices.forEach(index => {
      const letter = this.letters[index];
      if (letter.colorProgress < 1) {
        letter.colorProgress += 0.015;
        if (letter.colorProgress >= 1) {
          letter.colorProgress = 1;
          this.activeLetterIndices.delete(index);
        }

        const startRgb = this.parseColor(letter.originalColor);
        const endRgb = this.parseColor(letter.targetColor);

        if (startRgb && endRgb) {
          letter.color = this.interpolateColor(startRgb, endRgb, letter.colorProgress);
          needsRedraw = true;
          itemsToDraw.add(index);
        } else {
          letter.color = letter.targetColor;
          letter.colorProgress = 1;
          this.activeLetterIndices.delete(index);
          needsRedraw = true;
          itemsToDraw.add(index);
        }
      } else {
        this.activeLetterIndices.delete(index);
      }
    });

    if (needsRedraw && itemsToDraw.size > 0) {
      this.drawLetters(itemsToDraw);
    }
  }

  private animate = () => {
    const now = Date.now();
    if (now - this.lastGlitchTime >= this.ncGlitchSpeed) {
      this.updateLetters();
      if (!this.ncSmooth) {
        this.drawLetters(this.activeLetterIndices);
        this.activeLetterIndices.clear();
      }
      this.lastGlitchTime = now;
    }

    if (this.ncSmooth) {
      this.handleSmoothTransitions();
    }

    this.animationRef = requestAnimationFrame(this.animate);
  }
}
